import * as R from 'ramda';
import * as yup from 'yup';
import i18n from 'helpers/i18n';
import messages from './messages';
import useAsync from 'hooks/useAsync';
import { useState, useEffect } from 'react';
import { Select, TextArea } from '@teachstonellc/component-library';
import { SectionCollapse } from 'components/SectionCollapse';
import { QuestionAccordion, QuestionAccordionItem } from './QuestionAccordion';
import {
  useValidations,
  useServerFieldError,
  getSubsectionStatus
} from 'components/Measure/View/ValidationsContext';

import {
  getCustomQuestions,
  GetCustomQuestionsRes,
  answerCustomQuestion
} from 'actions/envScales/observationFeedback';

type CustomQuestion = GetCustomQuestionsRes['questions_with_answers'][number];
type QuestionState = { answer_notes: string } | { answer_id: number };

const requiredText = yup.string().required();
const requiredOption = yup.number().required();

interface CustomQuestionsSectionProps {
  assessmentId: number;
  attached?: boolean;
}

export function CustomQuestionsSection({
  attached,
  assessmentId
}: CustomQuestionsSectionProps) {
  const { run, isPending } = useAsync();
  const { sections, errors, resolvedPaths, showErrorStatus } = useValidations();
  const [expandedQuestions, setExpandedQuestions] = useState<string[]>([]);
  const [questions, setQuestions] = useState<GetCustomQuestionsRes>({
    questions_with_answers: []
  });
  const allAnswered = questions.questions_with_answers.every(q =>
    q.question_type === 'free_text' ? q.answer_notes?.length : q.answer_id
  );

  const status = !showErrorStatus
    ? allAnswered
      ? 'completed'
      : 'pending'
    : sections
      ? getSubsectionStatus(
          sections.evidence,
          'custom_questions',
          showErrorStatus,
          { errors, resolvedPaths, prefix: 'evidence.custom_questions' }
        )
      : 'pending';

  useEffect(() => {
    run(getCustomQuestions(assessmentId), {
      onSuccess: data => setQuestions(data)
    });
  }, [run, assessmentId]);

  function updateQuestionsState(questionId: number, state: QuestionState) {
    setQuestions(prevState => {
      const newState = prevState.questions_with_answers.map(obj =>
        obj.question_id === questionId ? { ...obj, ...state } : obj
      );

      return { questions_with_answers: newState };
    });
  }

  function handleAnswer(answer: string, questionId: number) {
    answerCustomQuestion(assessmentId, questionId, { answer });
  }

  function answerQuestionWithOption(questionId: number, optionId: number) {
    updateQuestionsState(questionId, { answer_id: optionId });

    answerCustomQuestion(assessmentId, questionId, {
      account_custom_question_option_id: optionId
    });
  }

  if (R.isEmpty(questions.questions_with_answers)) {
    return null;
  }

  return (
    <SectionCollapse
      title={i18n.ft(messages.title)}
      subtitle={i18n.ft(messages.subtitle.questions)}
      status={status}
      attached={attached}
      closeOthersOnOpen
    >
      {!isPending && (
        <QuestionAccordion
          value={expandedQuestions}
          onValueChange={setExpandedQuestions}
        >
          {questions.questions_with_answers.map(record => (
            <QuestionItem
              key={record.question_id}
              record={record}
              onAnswerText={handleAnswer}
              onAnswerTextChange={updateQuestionsState}
              onAnswerOption={answerQuestionWithOption}
            />
          ))}
        </QuestionAccordion>
      )}
    </SectionCollapse>
  );
}

interface QuestionItemProps {
  record: CustomQuestion;
  onAnswerText: (answer: string, questionId: number) => void;
  onAnswerTextChange: (questionId: number, state: QuestionState) => void;
  onAnswerOption: (questionId: number, optionId: number) => void;
}

function QuestionItem({
  record,
  onAnswerText,
  onAnswerTextChange,
  onAnswerOption
}: QuestionItemProps) {
  const { showErrorStatus } = useValidations();

  const isAnswered =
    record.question_type === 'free_text'
      ? record.answer_notes?.length > 0
      : Boolean(record.answer_id);

  const currentValue =
    record.question_type === 'free_text'
      ? record.answer_notes
      : record.answer_id;

  const schema =
    record.question_type === 'free_text' ? requiredText : requiredOption;

  const error = useServerFieldError(
    `evidence.custom_questions.${record.question_id}`,
    currentValue,
    schema
  );

  // Live check instead of relying on `error` alone, which only narrows an
  // already-known error and won't catch a field cleared after being valid.
  const hasError = showErrorStatus && !schema.isValidSync(currentValue);

  return (
    <QuestionAccordionItem
      questionId={record.question_id}
      question={record.question}
      isAnswered={isAnswered}
      error={hasError}
    >
      {record.question_type === 'free_text' ? (
        <TextArea
          rows={4}
          label={record.question}
          showLabel={false}
          aria-label={record.question}
          placeholder={i18n.ft(messages.textAreaPlaceholder)}
          data-question-id={record.question_id}
          defaultValue={record.answer_notes || ''}
          error={error?.message}
          onBlur={event =>
            onAnswerText(event.currentTarget.value, record.question_id)
          }
          onChange={event =>
            onAnswerTextChange(record.question_id, {
              answer_notes: event.currentTarget.value
            })
          }
        />
      ) : (
        // The component library renders a Select's label visibly and
        // offers no `showLabel`, so it is hidden here -- the question
        // is already stated by the trigger this content belongs to.
        <div className="[&_label]:sr-only">
          <Select
            label={i18n.ft(messages.answerLabel)}
            placeholder={i18n.ft(messages.selectPlaceholder)}
            value={record.answer_id}
            error={error?.message}
            onChange={value =>
              onAnswerOption(record.question_id, Number(value))
            }
            options={record.options.map(opt => ({
              value: opt.id,
              text: opt.option
            }))}
          />
        </div>
      )}
    </QuestionAccordionItem>
  );
}
